import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { Theme as MuiTheme, ThemeProvider } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';
import { createPipeexTheme } from '../../theme/theme';

// Define theme types
interface ThemeContextType {
  theme: MuiTheme;
  toggleTheme: () => void;
  mode: PaletteMode;
  setMode: (mode: PaletteMode) => void;
}

// Create context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

const ThemeModeStorageKey = 'themeMode';

const isPaletteMode = (value: string | null): value is PaletteMode =>
  value === 'light' || value === 'dark';

const getStoredThemeMode = (): PaletteMode | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const savedMode = window.localStorage.getItem(ThemeModeStorageKey);
    return isPaletteMode(savedMode) ? savedMode : null;
  } catch {
    return null;
  }
};

const getSystemThemeMode = (): PaletteMode => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'dark';
  }

  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
};

const persistThemeMode = (mode: PaletteMode): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(ThemeModeStorageKey, mode);
  } catch {
    // Theme selection is a preference only; ignore storage failures.
  }
};

const setDocumentThemeMode = (mode: PaletteMode): void => {
  if (typeof document === 'undefined') {
    return;
  }

  document.documentElement.dataset.pipeexColorMode = mode;
};

// Theme provider component
export const ThemeProviderWrapper: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<PaletteMode>(() => {
    return getStoredThemeMode() ?? getSystemThemeMode();
  });
  const [hasExplicitMode, setHasExplicitMode] = useState(() => getStoredThemeMode() !== null);

  const setPersistedMode = useCallback((newMode: PaletteMode) => {
    setHasExplicitMode(true);
    setMode(newMode);
    persistThemeMode(newMode);
  }, []);

  const toggleTheme = useCallback(() => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setPersistedMode(newMode);
  }, [mode, setPersistedMode]);

  useEffect(() => {
    setDocumentThemeMode(mode);
  }, [mode]);

  useEffect(() => {
    if (hasExplicitMode || typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
    const handleSystemThemeChange = (event: MediaQueryListEvent) => {
      setMode(event.matches ? 'light' : 'dark');
    };

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleSystemThemeChange);
      return () => {
        mediaQuery.removeEventListener('change', handleSystemThemeChange);
      };
    }

    mediaQuery.addListener(handleSystemThemeChange);
    return () => {
      mediaQuery.removeListener(handleSystemThemeChange);
    };
  }, [hasExplicitMode]);

  const theme = useMemo(() => createPipeexTheme(mode), [mode]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, mode, setMode: setPersistedMode }}>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

// Custom hook to use theme context
export const useThemeContext = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within a ThemeProviderWrapper');
  }
  return context;
};

export const useOptionalThemeContext = (): ThemeContextType | undefined => useContext(ThemeContext);
